/**
 * Jhango Product Card JavaScript
 * Compatible with Dawn theme's cart system (cart-notification, cart-drawer)
 */

(function() {
  'use strict';

  // Prevent duplicate initialization
  if (window.jhangoCardInitialized) return;
  window.jhangoCardInitialized = true;

  /**
   * Initialize Jhango Card functionality when DOM is ready
   */
  function initJhangoCards() {
    // Variant Selection Handler
    document.addEventListener('click', handleVariantClick);

    // Form Submit Handler
    document.addEventListener('submit', handleFormSubmit);

    // Dropup Selector Handler
    document.addEventListener('click', handleDropupClick);

    // Variant Dropdown Change Handler
    document.addEventListener('change', handleVariantDropdownChange);

    // Close dropups when clicking outside
    document.addEventListener('click', closeDropupsOnOutsideClick);

    // Initialize custom elements
    initQuickAddModalOpener();
    initDropupSelectors();
  }

  /**
   * Initialize Quick Add Modal Opener custom element
   */
  function initQuickAddModalOpener() {
    if (!customElements.get('quick-add-modal-opener')) {
      customElements.define('quick-add-modal-opener', class QuickAddModalOpener extends HTMLElement {
        constructor() {
          super();
          this.button = this.querySelector('button');
          if (!this.button) return;

          this.button.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal();
          });
        }

        openModal() {
          const modalSelector = this.getAttribute('data-modal');
          const modal = document.querySelector(modalSelector);
          if (!modal) return;

          // Check if Dawn's quick-add-modal has show method
          if (typeof modal.show === 'function') {
            modal.show(this.button);
            return;
          }

          // Fallback: Handle modal ourselves
          const productUrl = this.button.getAttribute('data-product-url');
          if (!productUrl) return;

          // Show loading state
          this.button.setAttribute('aria-disabled', 'true');
          this.button.classList.add('loading');

          // Store original parent to restore later
          this.originalParent = modal.parentElement;
          this.originalNextSibling = modal.nextElementSibling;

          // Move modal to body for full-page display
          document.body.appendChild(modal);

          // Fetch product page
          fetch(productUrl)
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const productInfo = doc.querySelector('product-info') || doc.querySelector('.product');

              if (productInfo) {
                const modalContent = modal.querySelector('.quick-add-modal__content-info');
                if (modalContent) {
                  modalContent.innerHTML = productInfo.outerHTML;

                  // Force load lazy images (main image and thumbnails)
                  modalContent.querySelectorAll('img[loading="lazy"]').forEach(img => {
                    img.loading = 'eager';
                    if (img.dataset.src) {
                      img.src = img.dataset.src;
                    } else if (img.srcset) {
                      const srcset = img.srcset;
                      img.srcset = '';
                      img.srcset = srcset;
                    }
                  });

                  // Disable scroll-trigger animations
                  modalContent.querySelectorAll('.scroll-trigger').forEach(el => {
                    el.style.animation = 'none';
                    el.style.opacity = '1';
                    el.style.transform = 'none';
                  });

                  // Initialize our own slider functionality
                  this.initModalSlider(modalContent);
                }
              }

              // Open modal
              modal.setAttribute('open', '');
              document.body.style.overflow = 'hidden';

              // Setup close handlers
              this.setupCloseHandlers(modal);

              // Init Shopify payment buttons if available
              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
            })
            .catch(err => {
              console.error('Jhango Quick View - Failed to load product:', err);
              // Move modal back on error
              this.restoreModal(modal);
            })
            .finally(() => {
              this.button.removeAttribute('aria-disabled');
              this.button.classList.remove('loading');
            });
        }

        initModalSlider(modalContent) {
          // Find all product images from various sources
          const images = [];

          // Get images from media items
          modalContent.querySelectorAll('.product__media-item img').forEach(img => {
            if (img.src || img.srcset) {
              images.push({
                src: img.src || img.currentSrc,
                srcset: img.srcset || '',
                alt: img.alt || ''
              });
            }
          });

          // If no images found, try other selectors
          if (images.length === 0) {
            modalContent.querySelectorAll('.product__media img, .product-media-container img').forEach(img => {
              if (img.src || img.srcset) {
                images.push({
                  src: img.src || img.currentSrc,
                  srcset: img.srcset || '',
                  alt: img.alt || ''
                });
              }
            });
          }

          // If only 1 or no images, don't create slider
          if (images.length <= 1) return;

          // Find media wrapper to replace with our slider
          const mediaWrapper = modalContent.querySelector('.product__media-wrapper');
          if (!mediaWrapper) return;

          // Create custom slider HTML
          const sliderHTML = `
            <div class="jhango-modal-slider">
              <div class="jhango-modal-slider__track">
                ${images.map((img, i) => `
                  <div class="jhango-modal-slider__slide ${i === 0 ? 'is-active' : ''}" data-index="${i}">
                    <img src="${img.src}" srcset="${img.srcset}" alt="${img.alt}" loading="eager">
                  </div>
                `).join('')}
              </div>
              <button class="jhango-modal-slider__arrow jhango-modal-slider__arrow--prev" type="button" aria-label="Previous">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button class="jhango-modal-slider__arrow jhango-modal-slider__arrow--next" type="button" aria-label="Next">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
              <div class="jhango-modal-slider__dots">
                ${images.map((_, i) => `
                  <button class="jhango-modal-slider__dot ${i === 0 ? 'is-active' : ''}" type="button" data-index="${i}" aria-label="Go to slide ${i + 1}"></button>
                `).join('')}
              </div>
            </div>
          `;

          // Replace media wrapper content
          mediaWrapper.innerHTML = sliderHTML;

          // Get slider elements
          const slider = mediaWrapper.querySelector('.jhango-modal-slider');
          const track = slider.querySelector('.jhango-modal-slider__track');
          const slides = slider.querySelectorAll('.jhango-modal-slider__slide');
          const dots = slider.querySelectorAll('.jhango-modal-slider__dot');
          const prevBtn = slider.querySelector('.jhango-modal-slider__arrow--prev');
          const nextBtn = slider.querySelector('.jhango-modal-slider__arrow--next');

          let currentIndex = 0;

          const showSlide = (index) => {
            // Wrap around
            if (index < 0) index = slides.length - 1;
            if (index >= slides.length) index = 0;
            currentIndex = index;

            // Update track position
            track.style.transform = `translateX(-${currentIndex * 100}%)`;

            // Update active states
            slides.forEach((slide, i) => {
              slide.classList.toggle('is-active', i === currentIndex);
            });
            dots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === currentIndex);
            });
          };

          // Arrow clicks
          prevBtn.addEventListener('click', () => showSlide(currentIndex - 1));
          nextBtn.addEventListener('click', () => showSlide(currentIndex + 1));

          // Dot clicks
          dots.forEach(dot => {
            dot.addEventListener('click', () => {
              showSlide(parseInt(dot.dataset.index));
            });
          });

          // Touch swipe
          let touchStartX = 0;
          track.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
          }, { passive: true });

          track.addEventListener('touchend', (e) => {
            const diff = touchStartX - e.changedTouches[0].screenX;
            if (Math.abs(diff) > 50) {
              if (diff > 0) showSlide(currentIndex + 1);
              else showSlide(currentIndex - 1);
            }
          }, { passive: true });
        }

        setupCloseHandlers(modal) {
          // Close button
          const closeBtn = modal.querySelector('.quick-add-modal__toggle');
          if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeModal(modal), { once: true });
          }

          // Click outside to close (on backdrop)
          const clickOutsideHandler = (e) => {
            if (e.target === modal) {
              this.closeModal(modal);
              modal.removeEventListener('click', clickOutsideHandler);
            }
          };
          modal.addEventListener('click', clickOutsideHandler);

          // Escape key to close
          const escHandler = (e) => {
            if (e.key === 'Escape') {
              this.closeModal(modal);
              document.removeEventListener('keydown', escHandler);
            }
          };
          document.addEventListener('keydown', escHandler);
        }

        restoreModal(modal) {
          if (this.originalParent) {
            if (this.originalNextSibling) {
              this.originalParent.insertBefore(modal, this.originalNextSibling);
            } else {
              this.originalParent.appendChild(modal);
            }
          }
        }

        closeModal(modal) {
          modal.removeAttribute('open');
          document.body.style.overflow = '';
          // Clear modal content
          const modalContent = modal.querySelector('.quick-add-modal__content-info');
          if (modalContent) {
            modalContent.innerHTML = '';
          }
          // Restore modal to original position
          this.restoreModal(modal);
        }
      });
    }
  }

  /**
   * Initialize Dropup Selector custom elements
   */
  function initDropupSelectors() {
    // Define custom element if not already defined
    if (!customElements.get('jhango-dropup-selector')) {
      customElements.define('jhango-dropup-selector', class JhangoDropupSelector extends HTMLElement {
        constructor() {
          super();
          this.trigger = this.querySelector('[data-dropdown-trigger]');
          this.menu = this.querySelector('[data-dropdown-menu]');
          this.options = this.querySelectorAll('.jhango-card__dropup-option');

          // Find form - either inside dropup or in parent card
          this.form = this.querySelector('.jhango-card__form') ||
                      this.closest('.jhango-card')?.querySelector('.jhango-card__form');
          this.variantInput = this.form?.querySelector('.jhango-card__variant-input');

          this.bindEvents();
        }

        bindEvents() {
          // Toggle dropdown on trigger click
          this.trigger?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggle();
          });

          // Handle option selection
          this.options.forEach(option => {
            option.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.selectOption(option);
            });
          });
        }

        toggle() {
          if (this.classList.contains('is-open')) {
            this.close();
          } else {
            // Close all other dropups first
            document.querySelectorAll('jhango-dropup-selector.is-open').forEach(dropup => {
              if (dropup !== this) dropup.close();
            });
            this.open();
          }
        }

        open() {
          this.classList.add('is-open');
          this.trigger?.setAttribute('aria-expanded', 'true');
          if (window.innerWidth <= 749) {
            this._savedScrollY = window.scrollY;
            /* Move overlay + menu to body so they aren't clipped */
            this._overlay = this.querySelector('.jhango-card__dropup-overlay');
            this._menu = this.querySelector('.jhango-card__dropup-menu');
            if (this._overlay) document.body.appendChild(this._overlay);
            if (this._menu) document.body.appendChild(this._menu);
            /* Show them (is-open is on this element, but now children are in body) */
            if (this._overlay) { this._overlay.style.opacity = '1'; this._overlay.style.visibility = 'visible'; }
            if (this._menu) { this._menu.style.maxHeight = '55vh'; this._menu.style.opacity = '1'; this._menu.style.visibility = 'visible'; this._menu.style.overflow = 'auto'; }
            document.body.style.overflow = 'hidden';
            /* Close on overlay tap */
            if (this._overlay) {
              this._overlayHandler = () => this.close();
              this._overlay.addEventListener('click', this._overlayHandler, { once: true });
            }
          }
        }

        close() {
          this.classList.remove('is-open');
          this.trigger?.setAttribute('aria-expanded', 'false');
          if (this._overlay && this._overlay.parentNode === document.body) {
            this._overlay.style.opacity = '';
            this._overlay.style.visibility = '';
            this.insertBefore(this._overlay, this.querySelector('.jhango-card__dropup-menu') || this.firstChild);
          }
          if (this._menu && this._menu.parentNode === document.body) {
            this._menu.style.maxHeight = '';
            this._menu.style.opacity = '';
            this._menu.style.visibility = '';
            this._menu.style.overflow = '';
            var overlayEl = this.querySelector('.jhango-card__dropup-overlay');
            if (overlayEl) {
              overlayEl.insertAdjacentElement('afterend', this._menu);
            } else {
              this.insertBefore(this._menu, this.querySelector('form') || null);
            }
          }
          document.body.style.overflow = '';
          if (this._savedScrollY !== undefined) {
            window.scrollTo(0, this._savedScrollY);
            this._savedScrollY = undefined;
          }
        }

        selectOption(option) {
          if (option.disabled || option.classList.contains('jhango-card__dropup-option--unavailable')) return;

          const variantId = option.dataset.variantId;
          if (!variantId) return;

          // Get original content
          const titleEl = option.querySelector('.jhango-card__dropup-title');
          const priceEl = option.querySelector('.jhango-card__dropup-price');
          const originalTitle = titleEl ? titleEl.textContent : '';
          const priceText = priceEl ? priceEl.textContent : '';

          // Close dropdown
          this.close();

          // Show loading state with "Adding..." and price
          option.classList.add('loading');
          if (titleEl) titleEl.textContent = 'Adding...';

          // Add to cart directly
          this.addToCart(variantId, option, originalTitle);
        }

        addToCart(variantId, option, originalTitle) {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);

          const titleEl = option.querySelector('.jhango-card__dropup-title');

          // Get cart element for section rendering
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cart && typeof cart.getSectionsToRender === 'function') {
            formData.append('sections', cart.getSectionsToRender().map(section => section.id));
            formData.append('sections_url', window.location.pathname);
          }

          const cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';

          fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Accept': 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          })
          .then(response => response.json())
          .then(response => {
            option.classList.remove('loading');

            if (response.status) {
              // Error - restore title and show error
              if (titleEl && originalTitle) titleEl.textContent = originalTitle;
              console.error('Dropup add to cart error:', response.description);
              option.classList.add('error');
              setTimeout(() => option.classList.remove('error'), 2000);
              return;
            }

            // Success - show "Added!" then restore
            if (titleEl) titleEl.textContent = 'Added!';
            option.classList.add('added');

            setTimeout(() => {
              option.classList.remove('added');
              if (titleEl && originalTitle) titleEl.textContent = originalTitle;
            }, 2000);

            // Publish cart update event
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'jhango-dropup',
                productVariantId: variantId,
                cartData: response
              });
            }

            // Update cart UI
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(response);
            } else {
              updateCartCount();
            }

            // Remove is-empty class from cart
            if (cart && cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          })
          .catch(error => {
            console.error('Dropup add to cart error:', error);
            option.classList.remove('loading');
            if (titleEl && originalTitle) titleEl.textContent = originalTitle;
            option.classList.add('error');
            setTimeout(() => option.classList.remove('error'), 2000);
          });
        }
      });
    }
  }

  /**
   * Handle dropup trigger clicks (for non-custom-element approach)
   */
  function handleDropupClick(e) {
    const trigger = e.target.closest('[data-dropdown-trigger]');
    if (!trigger) return;

    const dropup = trigger.closest('.jhango-card__dropup');
    if (!dropup || dropup.tagName === 'JHANGO-DROPUP-SELECTOR') return; // Skip if custom element

    e.preventDefault();
    e.stopPropagation();

    var wasOpen = dropup.classList.contains('is-open');
    dropup.classList.toggle('is-open');
    if (!wasOpen && window.innerWidth <= 749) {
      dropup._savedScrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + dropup._savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
    } else if (wasOpen) {
      unlockScroll(dropup._savedScrollY);
    }
  }

  /**
   * Close dropups when clicking outside
   */
  function unlockScroll(savedY) {
    if (document.body.style.position === 'fixed') {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, savedY || 0);
    }
  }

  function closeDropupsOnOutsideClick(e) {
    if (e.target.closest('.jhango-card__dropup')) return;

    document.querySelectorAll('.jhango-card__dropup.is-open').forEach(dropup => {
      dropup.classList.remove('is-open');
      var trigger = dropup.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      unlockScroll(dropup._savedScrollY);
    });
  }

  /**
   * Handle variant dropdown (select) changes
   */
  function handleVariantDropdownChange(e) {
    const select = e.target.closest('.jhango-card__variant-select');
    if (!select) return;

    const form = select.closest('.jhango-card__form');
    if (!form) return;

    const selectedOption = select.options[select.selectedIndex];
    const price = selectedOption?.dataset?.variantPrice;
    const unitPrice = selectedOption?.dataset?.variantUnitPrice;

    const priceEl = form.querySelector('.jhango-card__price-value');
    if (priceEl && price) {
      priceEl.textContent = price;
    }

    const card = form.closest('.jhango-card');
    const unitBadge = card?.querySelector('.jhango-card__unit-badge-text');
    if (unitBadge) {
      if (unitPrice) {
        unitBadge.innerHTML = unitPrice;
        unitBadge.closest('.jhango-card__unit-badge').style.display = '';
      } else {
        unitBadge.closest('.jhango-card__unit-badge').style.display = 'none';
      }
    }
  }

  /**
   * Handle variant button clicks
   * @param {Event} e - Click event
   */
  function handleVariantClick(e) {
    const btn = e.target.closest('.jhango-card__variant');
    if (!btn || btn.disabled || btn.classList.contains('jhango-card__variant--active')) return;

    const card = btn.closest('.jhango-card');
    if (!card) return;

    const form = card.querySelector('.jhango-card__form');
    const variantInput = form.querySelector('.jhango-card__variant-input');
    const atcBtn = form.querySelector('.jhango-card__atc');
    const atcTextFull = atcBtn.querySelector('.jhango-card__atc-full');
    const atcTextShort = atcBtn.querySelector('.jhango-card__atc-short');
    const priceEl = atcBtn.querySelector('.jhango-card__price-value');
    const unitBadge = card.querySelector('.jhango-card__unit-badge-text');

    // Remove active class from all variants in this card
    card.querySelectorAll('.jhango-card__variant').forEach(b => {
      b.classList.remove('jhango-card__variant--active');
    });

    // Add active class to clicked variant
    btn.classList.add('jhango-card__variant--active');

    // Update hidden input
    variantInput.value = btn.dataset.variantId;

    // Update price display
    const price = btn.dataset.variantPrice;
    if (priceEl && price) {
      priceEl.innerHTML = price;
    }

    // Update unit badge — show/hide based on native Shopify unit_price
    if (unitBadge) {
      const unitPrice = btn.dataset.variantUnitPrice;
      if (unitPrice) {
        unitBadge.innerHTML = unitPrice;
        unitBadge.closest('.jhango-card__unit-badge').style.display = '';
      } else {
        unitBadge.closest('.jhango-card__unit-badge').style.display = 'none';
      }
    }

    // Update button state based on availability
    const available = btn.dataset.variantAvailable === 'true';
    atcBtn.disabled = !available;

    const addToCartText = window.variantStrings?.addToCart || 'Add to Cart';
    const soldOutText = window.variantStrings?.soldOut || 'Sold Out';

    if (atcTextFull) {
      atcTextFull.textContent = available ? addToCartText : soldOutText;
    }
    if (atcTextShort) {
      atcTextShort.textContent = available ? 'Add' : 'Sold';
    }
  }

  /**
   * Get fetch configuration matching Dawn theme's pattern
   */
  function getFetchConfig() {
    return {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
  }

  /**
   * Handle form submission with AJAX
   * @param {Event} e - Submit event
   */
  function handleFormSubmit(e) {
    const form = e.target.closest('.jhango-card__form');
    if (!form) return;

    e.preventDefault();

    const btn = form.querySelector('.jhango-card__atc');
    if (!btn || btn.disabled || btn.classList.contains('loading')) return;

    const btnTextFull = btn.querySelector('.jhango-card__atc-full');
    const btnTextShort = btn.querySelector('.jhango-card__atc-short');
    const originalFull = btnTextFull ? btnTextFull.textContent : 'Add to Cart';
    const originalShort = btnTextShort ? btnTextShort.textContent : 'Add';
    const variantId = form.querySelector('.jhango-card__variant-input').value;

    // Set loading state with "Adding..." text
    btn.disabled = true;
    btn.classList.add('loading');
    if (btnTextFull) btnTextFull.textContent = 'Adding...';
    if (btnTextShort) btnTextShort.textContent = 'Adding...';

    // Get cart element (Dawn theme uses cart-notification or cart-drawer)
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Prepare form data
    const formData = new FormData(form);

    // If we have a cart element, append sections to render and set active element
    if (cart) {
      if (typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }
      // Set active element for focus management
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    // Get fetch config matching Dawn theme
    const config = getFetchConfig();
    config.body = formData;

    // Get cart add URL from routes (set in theme.liquid)
    const cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';

    // Make the request
    fetch(cartAddUrl, config)
    .then(response => response.json())
    .then(response => {
      if (response.status) {
        // Error response - publish cart error event
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'jhango-card',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message
          });
        }
        handleError(btn, btnTextFull, btnTextShort, response.description || 'Error adding to cart');

        setTimeout(() => {
          btn.disabled = false;
          btn.classList.remove('loading');
          if (btnTextFull) btnTextFull.textContent = originalFull;
          if (btnTextShort) btnTextShort.textContent = originalShort;
        }, 2000);
        return;
      }

      // Success - show added state
      if (btnTextFull) btnTextFull.textContent = 'Added!';
      if (btnTextShort) btnTextShort.textContent = '✓';
      btn.classList.add('jhango-card__atc--added');

      // Publish cart update event for Dawn theme
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'jhango-card',
          productVariantId: variantId,
          cartData: response
        });
      }

      // Update cart UI
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
      } else {
        // Fallback: Update cart count manually
        updateCartCount();
      }

      // Remove is-empty class from cart
      if (cart && cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }

      // Reset button after delay
      setTimeout(() => {
        btn.disabled = false;
        btn.classList.remove('loading', 'jhango-card__atc--added');
        if (btnTextFull) btnTextFull.textContent = originalFull;
        if (btnTextShort) btnTextShort.textContent = originalShort;
      }, 2000);
    })
    .catch(error => {
      console.error('Jhango Card - Cart error:', error);
      handleError(btn, btnTextFull, btnTextShort, 'Error');

      setTimeout(() => {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (btnTextFull) btnTextFull.textContent = originalFull;
        if (btnTextShort) btnTextShort.textContent = originalShort;
      }, 2000);
    });
  }

  /**
   * Handle error state
   */
  function handleError(btn, btnTextFull, btnTextShort, message) {
    btn.classList.remove('loading');
    if (btnTextFull) btnTextFull.textContent = 'Error';
    if (btnTextShort) btnTextShort.textContent = '!';
    console.error('Jhango Card:', message);
  }

  /**
   * Update cart count in header (fallback method)
   */
  function updateCartCount() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
    .then(response => response.json())
    .then(cart => {
      // Update cart count elements (Dawn theme selectors)
      const cartCountElements = document.querySelectorAll(
        '.cart-count-bubble span[aria-hidden], [data-cart-count], #cart-icon-bubble span'
      );
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        // Show the bubble if it was hidden
        const bubble = el.closest('.cart-count-bubble');
        if (bubble && cart.item_count > 0) {
          bubble.removeAttribute('hidden');
        }
      });
    })
    .catch(err => console.error('Failed to update cart count:', err));
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initJhangoCards);
  } else {
    initJhangoCards();
  }
})();
